<template>
  <el-dialog
    class="database_creator_and_editor"
    width="640px"
    :title="calcDialogTitle"
    :close-on-click-modal="false"
    :visible.sync="isVisible"
    @closed="onDialogClosed"
  >
    <el-form
      size="mini"
      ref="form"
      label-width="108px"
      v-loading="isLoading"
      :model="form"
      :rules="formRules"
    >
      <el-form-item label="实例名称" prop="name">
        <el-input v-model="form.name"></el-input>
      </el-form-item>
      <el-form-item label="数据库类型" prop="type">
        <el-radio-group v-model="form.type" @change="handleDatabaseTypeChanged">
          <el-radio-button
            v-for="(database, index) in options.database"
            :key="index"
            :label="database.value"
            :disabled="dialogType === 'edit'"
            >{{ database.label }}</el-radio-button
          >
        </el-radio-group>
      </el-form-item>
      <el-form-item label="登录地址" prop="address">
        <el-input v-model="form.address"></el-input>
      </el-form-item>
      <el-form-item label="端口" prop="port">
        <el-input-number v-model="form.port"></el-input-number>
      </el-form-item>
      <el-form-item label="数据库名" prop="databaseName" v-if="form.type === 'mongodb'">
        <el-input v-model="form.databaseName"></el-input>
      </el-form-item>
      <el-form-item
        label="用户名"
        prop="username"
        v-show="form.type !== 'redis'"
        :required="form.type !== 'redis'"
      >
        <el-input v-model="form.username"></el-input>
      </el-form-item>
      <el-form-item label="密码">
        <el-input v-model="form.password" type="password"></el-input>
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="isVisible = false">取消</el-button>
      <el-button type="primary" @click="onTestConnection">连通性测试</el-button>
      <el-button type="primary" :disabled="!isTested" @click="onSubmit">提交</el-button>
    </template>
  </el-dialog>
</template>

<script>
import { reqCreateDataSource, reqTestDataSource, reqEditDataSource } from '@/api/source';
import Validator from '@/utils/Validator';
import * as _cloneDeep from 'lodash/cloneDeep';

export default {
  name: 'DatabaseCreatorAndEditor',

  data() {
    const usernameValidator = (rule, value, callback) => {
      if (this.form.type !== 'redis') {
        if (!value) {
          callback(new Error('用户名必填'));
          return;
        }
      }
      callback();
    };

    const portValidator = (rule, value, callback) => {
      const validator = new Validator(rule, value, callback);
      validator
        .register(validator.isNumber())
        .register(validator.range(1024, 65536, `端口号必须在 1024 - 65535 之间`))
        .validate();
    };

    const databaseNameValidator = (rule, value, callback) => {
      const validator = new Validator(rule, value, callback);
      validator.register(validator.isStandardStr()).validate();
    };

    return {
      isTested: false,
      isVisible: false,
      isLoading: false,
      dialogType: '',
      form: {
        id: 0,
        name: '',
        type: 'mysql',
        address: '',
        port: 3306,
        databaseName: '',
        username: '',
        password: ''
      },
      formRules: {
        name: { required: true, message: '数据库名称必选', trigger: 'change' },
        type: { required: true, message: '数据库类型必选', trigger: 'change' },
        address: [{ required: true, message: '地址必填', trigger: 'blur' }],
        port: [
          { required: true, message: '端口必填', trigger: 'change' },
          { validator: portValidator, trigger: 'change' }
        ],
        databaseName: [{ validator: databaseNameValidator, trigger: 'blur' }],
        username: [{ validator: usernameValidator, trigger: 'blur' }]
      },
      options: {
        database: [
          { label: 'MySQL', value: 'mysql' },
          { label: 'MongoDB', value: 'mongodb' },
          { label: 'Redis', value: 'redis' }
        ]
      }
    };
  },

  computed: {
    calcDialogTitle() {
      return this.dialogType === 'create' ? '连接数据库' : '编辑数据库';
    }
  },

  methods: {
    open({ dialogType, form }) {
      this.dialogType = dialogType;
      if (this.dialogType === 'edit') {
        this.form.id = form.id;
        this.form.name = form.name;
        this.form.type = form.dbType;
        this.form.address = form.ip;
        this.form.port = form.port;
        this.form.username = form.username;
        this.form.password = form.password;
        if (form.dbType === 'mongodb') {
          this.form.databaseName = form.suffix;
        }
      }

      this.isVisible = true;
    },
    onDialogClosed() {
      this.form = _cloneDeep({
        id: 0,
        name: '',
        type: 'mysql',
        address: '',
        port: 3306,
        databaseName: '',
        username: '',
        password: ''
      });
      this.$refs.form.resetFields();
      this.isTested = false;
    },
    handleDatabaseTypeChanged(val) {
      const relations = {
        mysql: 3306,
        mongodb: 27017,
        redis: 6379
      };

      this.form.port = relations[val];
    },
    buildRequestData() {
      const reqData = {
        name: this.form.name,
        port: this.form.port,
        ip: this.form.address,
        password: this.form.password,
        dbType: this.form.type,
        username: ['mysql', 'mongodb'].includes(this.form.type) ? this.form.username : ''
      };
      // 编辑时添加 id
      if (this.dialogType === 'edit') {
        reqData.id = this.form.id;
      }
      // mongodb 数据库必须填写一个数据库名
      if (this.form.type === 'mongodb') {
        reqData.suffix = this.form.databaseName;
      }

      return reqData;
    },
    onTestConnection() {
      this.$refs.form.validate(async valid => {
        if (!valid) {
          this.$message.warning('请完善所有表单项');
          return;
        }

        try {
          this.isLoading = true;

          const res = await reqTestDataSource(this.buildRequestData());
          if (res.data !== 0) {
            this.$message.error('连接失败，请检查配置信息');
            return;
          }
          this.$message.success('测试通过');
          this.isTested = true;
        } catch (e) {
          this.$message.error('连接失败，请重试');
        } finally {
          this.isLoading = false;
        }
      });
    },
    onSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) {
          this.$message.warning('请完善所有表单项');
          return;
        }

        try {
          this.isLoading = true;

          const api = this.dialogType === 'create' ? reqCreateDataSource : reqEditDataSource;
          await api(this.buildRequestData());

          this.$message.success(this.dialogType === 'create' ? '添加成功' : '编辑成功');
          this.isVisible = false;
          this.$emit('submit', true);
        } catch (e) {
          this.$message.error(this.dialogType === 'create' ? '添加失败' : '编辑失败');
          this.$emit('submit', false);
        } finally {
          this.isLoading = false;
        }
      });
    }
  }
};
</script>
